'use client';

import { createContext, useContext } from 'react';
import { useParentPageContext } from '@/creditflowresearch/application/ParentPageContext';
import { withDealByProductContext } from '@/creditflowresearch/ui/components/deals/DealTiles/withDealByProductContext';
import { withTileContext } from '@/creditflowresearch/ui/components/deals/DealTiles/withTileContexts';
import { useProductsContext } from '@/creditflowresearch/application/Deal/contexts/ProductsContext';
import { useScorecard } from '@/creditflowresearch/application/League/Scorecard/useScorecard';
import { IScorecardCompanyData } from '@/creditflowresearch/application/League/interfaces';
import { IStandingsResponse } from '@/creditflowresearch/domain/vo/League/IStandings';
import { TPageFiltersAdditionalFilters } from '@/creditflowresearch/application/League/Scorecard/types';
import { getDealsByActiveProduct } from '@/creditflowresearch/application/Deal/helpers/getDealsByActiveProduct';

type ScorecardContextType = ReturnType<typeof useScorecard>;

export const ScorecardContext = createContext<ScorecardContextType>(null);

export const useScorecardContext = () => {
  const context = useContext(ScorecardContext);

  if (!context) {
    throw new Error('useScorecardContext must be used within the ScorecardContext');
  }

  return context;
};

interface IProps {
  scorecardData: IStandingsResponse,
  companyData: IScorecardCompanyData,
  additionalFilters?: TPageFiltersAdditionalFilters,
  children: React.ReactNode,
}

const ScorecardContextProvider = ({ scorecardData, companyData, additionalFilters, children }: IProps) => {
  const { deals } = useParentPageContext();
  const { activeProduct } = useProductsContext();
  const productDeals = getDealsByActiveProduct(deals, activeProduct);
  return (
    <ScorecardContext.Provider value={ useScorecard({
      scorecardData,
      companyData,
      filtersByProduct: productDeals?.pageFilters,
      additionalFilters,
      children: null,
    }) }
    >
      { children }
    </ScorecardContext.Provider>
  );
};

export default withTileContext(withDealByProductContext(ScorecardContextProvider));
